import React from 'react';

interface ScreeningProgressNoteProps {
  /** Structurally compatible with the ScreeningProgress type in MasterTable / LensesTable. */
  progress?: {
    progressPercentage?: number;
  } | null;
}

/**
 * Body text for the screening blur overlay.
 *
 * The entity service records progress once per OpenSearch sub-batch
 * (SCREENING_BATCH_SIZE, 15 by default — see services/entity/src/workers/
 * screening.worker.ts), so a database holding fewer entities than one batch
 * reports 0% for the whole run. Rendering `${percent}%` unconditionally then
 * showed a counter frozen at "0%" for minutes, which reads as a hung job.
 *
 * So only show a number once it can actually move, and otherwise say what is
 * being worked on and let the spinner carry the "still running" signal.
 */
const ScreeningProgressNote: React.FC<ScreeningProgressNoteProps> = ({
  progress,
}) => {
  const percent = progress?.progressPercentage ?? 0;

  if (percent > 0) {
    return <p>{`This could take a few minutes. ${percent}%`}</p>;
  }

  // Deliberately does not name a unit. `chunksTotal` is entity totals on the
  // live path (screeningStatus.run.entitiesTotal) but processing chunks on the
  // history fallback (latestScreening.chunk_total), so labelling it "entities"
  // under-reported batched screens by the batch size. Show the count only where
  // it is unambiguous — as progress out of a total — and otherwise say nothing.
  return (
    <p className='screening-progress-indeterminate'>
      <span className='screening-progress-spinner' aria-hidden='true' />
      This could take a few minutes.
    </p>
  );
};

export default ScreeningProgressNote;
